import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Flex,
  Spacer,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaGlobe } from 'react-icons/fa';
import { api } from '../../services/api';

interface Language {
  id: string;
  name: string;
  native_name: string;
  code: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
}

interface Translation {
  id: number;
  key: string;
  language_code: string;
  value: string;
  context: string;
  language?: Language;
}

const I18nAdminPage: React.FC = () => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedContext, setSelectedContext] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  
  // Modal states
  const { isOpen: isLangModalOpen, onOpen: onLangModalOpen, onClose: onLangModalClose } = useDisclosure();
  const { isOpen: isTransModalOpen, onOpen: onTransModalOpen, onClose: onTransModalClose } = useDisclosure();
  const [editingItem, setEditingItem] = useState<any>(null);
  
  const toast = useToast();

  // Fetch languages
  const fetchLanguages = async () => {
    try {
      const response = await api.get('/admin/i18n/languages');
      setLanguages(response.data.languages);
    } catch (error) {
      toast({
        title: 'Chyba při načítání jazyků',
        description: 'Nepodařilo se načíst seznam jazyků',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Fetch translations
  const fetchTranslations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      
      if (selectedLanguage) params.append('language', selectedLanguage);
      if (selectedContext) params.append('context', selectedContext);
      
      const response = await api.get(`/admin/i18n/translations?${params}`);
      setTranslations(response.data.translations);
      setTotal(response.data.total);
    } catch (error) {
      toast({
        title: 'Chyba při načítání překladů',
        description: 'Nepodařilo se načíst seznam překladů',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save language
  const saveLanguage = async (languageData: Partial<Language>) => {
    try {
      if (editingItem?.id) {
        await api.put(`/admin/i18n/languages/${editingItem.id}`, languageData);
        toast({
          title: 'Jazyk aktualizován',
          description: 'Jazyk byl úspěšně aktualizován',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.post('/admin/i18n/languages', languageData);
        toast({
          title: 'Jazyk vytvořen',
          description: 'Nový jazyk byl úspěšně vytvořen',
          status: 'success',
          duration: 3000,
        });
      }
      await fetchLanguages();
      onLangModalClose();
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Chyba při ukládání jazyka',
        description: 'Nepodařilo se uložit jazyk',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Save translation
  const saveTranslation = async (translationData: Partial<Translation>) => {
    try {
      if (editingItem?.id) {
        await api.put(`/admin/i18n/translations/${editingItem.id}`, translationData);
        toast({
          title: 'Překlad aktualizován',
          description: 'Překlad byl úspěšně aktualizován',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.post('/admin/i18n/translations', translationData);
        toast({
          title: 'Překlad vytvořen',
          description: 'Nový překlad byl úspěšně vytvořen',
          status: 'success',
          duration: 3000,
        });
      }
      await fetchTranslations();
      onTransModalClose();
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Chyba při ukládání překladu',
        description: 'Nepodařilo se uložit překlad',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Delete language
  const deleteLanguage = async (id: string) => {
    if (!confirm('Opravdu chcete smazat tento jazyk?')) return;
    
    try {
      await api.delete(`/admin/i18n/languages/${id}`);
      toast({
        title: 'Jazyk smazán',
        description: 'Jazyk byl úspěšně smazán',
        status: 'success',
        duration: 3000,
      });
      await fetchLanguages();
    } catch (error) {
      toast({
        title: 'Chyba při mazání jazyka',
        description: 'Nepodařilo se smazat jazyk',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Delete translation
  const deleteTranslation = async (id: number) => {
    if (!confirm('Opravdu chcete smazat tento překlad?')) return;
    
    try {
      await api.delete(`/admin/i18n/translations/${id}`);
      toast({
        title: 'Překlad smazán',
        description: 'Překlad byl úspěšně smazán',
        status: 'success',
        duration: 3000,
      });
      await fetchTranslations();
    } catch (error) {
      toast({
        title: 'Chyba při mazání překladu',
        description: 'Nepodařilo se smazat překlad',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Filter translations
  const filteredTranslations = translations.filter(translation => {
    const matchesSearch = translation.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         translation.value.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    fetchTranslations();
  }, [selectedLanguage, selectedContext, currentPage]);

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Heading size="lg" display="flex" alignItems="center" gap={2}>
            <FaGlobe />
            Správa překladů
          </Heading>
          <HStack spacing={2}>
            <Button colorScheme="blue" onClick={onLangModalOpen} leftIcon={<FaPlus />}>
              Přidat jazyk
            </Button>
            <Button colorScheme="green" onClick={onTransModalOpen} leftIcon={<FaPlus />}>
              Přidat překlad
            </Button>
          </HStack>
        </Flex>

        {/* Languages Section */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Heading size="md" mb={4}>Jazyky</Heading>
          <Table>
            <Thead>
              <Tr>
                <Th>Kód</Th>
                <Th>Název</Th>
                <Th>Domorodý název</Th>
                <Th>Výchozí</Th>
                <Th>Aktivní</Th>
                <Th>Pořadí</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {languages.map((lang) => (
                <Tr key={lang.id}>
                  <Td>
                    <Badge colorScheme={lang.is_default ? 'blue' : 'gray'}>
                      {lang.code}
                    </Badge>
                  </Td>
                  <Td>{lang.name}</Td>
                  <Td>{lang.native_name}</Td>
                  <Td>
                    <Badge colorScheme={lang.is_default ? 'green' : 'gray'}>
                      {lang.is_default ? 'Ano' : 'Ne'}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={lang.is_active ? 'green' : 'red'}>
                      {lang.is_active ? 'Ano' : 'Ne'}
                    </Badge>
                  </Td>
                  <Td>{lang.sort_order}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Upravit">
                        <IconButton
                          aria-label="Upravit"
                          icon={<FaEdit />}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingItem(lang);
                            onLangModalOpen();
                          }}
                        />
                      </Tooltip>
                      {!lang.is_default && (
                        <Tooltip label="Smazat">
                          <IconButton
                            aria-label="Smazat"
                            icon={<FaTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => deleteLanguage(lang.id)}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Translations Section */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <VStack spacing={4} align="stretch" mb={6}>
            <Heading size="md">Překlady</Heading>
            
            {/* Filters */}
            <HStack spacing={4}>
              <Select
                placeholder="Všechny jazyky"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                maxW="200px"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.native_name}
                  </option>
                ))}
              </Select>
              
              <Select
                placeholder="Všechny kontexty"
                value={selectedContext}
                onChange={(e) => setSelectedContext(e.target.value)}
                maxW="200px"
              >
                <option value="common">Obecné</option>
                <option value="nav">Navigace</option>
                <option value="admin">Admin</option>
                <option value="form">Formuláře</option>
                <option value="content">Obsah</option>
                <option value="match">Zápasy</option>
                <option value="team">Týmy</option>
                <option value="gallery">Galerie</option>
                <option value="search">Vyhledávání</option>
                <option value="pagination">Stránkování</option>
                <option value="date">Datum</option>
                <option value="auth">Autentizace</option>
                <option value="error">Chyby</option>
              </Select>
              
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <FaSearch />
                </InputLeftElement>
                <Input
                  placeholder="Hledat překlad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </HStack>
          </VStack>

          <Table>
            <Thead>
              <Tr>
                <Th>Klíč</Th>
                <Th>Jazyk</Th>
                <Th>Hodnota</Th>
                <Th>Kontext</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Text>Načítání...</Text>
                  </Td>
                </Tr>
              ) : filteredTranslations.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Text>Žádné překlady</Text>
                  </Td>
                </Tr>
              ) : (
                filteredTranslations.map((translation) => (
                  <Tr key={translation.id}>
                    <Td>
                      <Text fontFamily="mono" fontSize="sm">
                        {translation.key}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {translation.language_code}
                      </Badge>
                    </Td>
                    <Td>
                      <Text maxW="300px" noOfLines={2}>
                        {translation.value}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="gray">
                        {translation.context || '—'}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="Upravit">
                          <IconButton
                            aria-label="Upravit"
                            icon={<FaEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingItem(translation);
                              onTransModalOpen();
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Smazat">
                          <IconButton
                            aria-label="Smazat"
                            icon={<FaTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => deleteTranslation(translation.id)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>

          {/* Pagination */}
          {total > pageSize && (
            <Flex justify="center" mt={4}>
              <HStack spacing={2}>
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  isDisabled={currentPage === 1}
                >
                  Předchozí
                </Button>
                <Text>
                  Strana {currentPage} z {Math.ceil(total / pageSize)}
                </Text>
                <Button
                  onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                  isDisabled={currentPage >= Math.ceil(total / pageSize)}
                >
                  Další
                </Button>
              </HStack>
            </Flex>
          )}
        </Box>
      </VStack>

      {/* Language Modal */}
      <Modal isOpen={isLangModalOpen} onClose={onLangModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingItem?.id ? 'Upravit jazyk' : 'Přidat jazyk'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>ID</FormLabel>
                <Input
                  defaultValue={editingItem?.id || ''}
                  placeholder="cs, en, de..."
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Název</FormLabel>
                <Input
                  defaultValue={editingItem?.name || ''}
                  placeholder="Czech, English..."
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Domorodý název</FormLabel>
                <Input
                  defaultValue={editingItem?.native_name || ''}
                  placeholder="Čeština, English..."
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Kód</FormLabel>
                <Input
                  defaultValue={editingItem?.code || ''}
                  placeholder="cs, en, de..."
                />
              </FormControl>
              <FormControl>
                <FormLabel>Pořadí</FormLabel>
                <Input
                  type="number"
                  defaultValue={editingItem?.sort_order || 0}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onLangModalClose}>
              Zrušit
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                const form = document.querySelector('form') as HTMLFormElement;
                const formData = new FormData(form);
                const data = Object.fromEntries(formData.entries());
                saveLanguage(data);
              }}
            >
              Uložit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Translation Modal */}
      <Modal isOpen={isTransModalOpen} onClose={onTransModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingItem?.id ? 'Upravit překlad' : 'Přidat překlad'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Klíč</FormLabel>
                <Input
                  defaultValue={editingItem?.key || ''}
                  placeholder="nav.home, form.required..."
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Jazyk</FormLabel>
                <Select
                  defaultValue={editingItem?.language_code || ''}
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.native_name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Hodnota</FormLabel>
                <Textarea
                  defaultValue={editingItem?.value || ''}
                  placeholder="Přeložený text..."
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Kontext</FormLabel>
                <Select
                  defaultValue={editingItem?.context || ''}
                >
                  <option value="">Žádný</option>
                  <option value="common">Obecné</option>
                  <option value="nav">Navigace</option>
                  <option value="admin">Admin</option>
                  <option value="form">Formuláře</option>
                  <option value="content">Obsah</option>
                  <option value="match">Zápasy</option>
                  <option value="team">Týmy</option>
                  <option value="gallery">Galerie</option>
                  <option value="search">Vyhledávání</option>
                  <option value="pagination">Stránkování</option>
                  <option value="date">Datum</option>
                  <option value="auth">Autentizace</option>
                  <option value="error">Chyby</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onTransModalClose}>
              Zrušit
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                const form = document.querySelector('form') as HTMLFormElement;
                const formData = new FormData(form);
                const data = Object.fromEntries(formData.entries());
                saveTranslation(data);
              }}
            >
              Uložit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default I18nAdminPage;
